import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { HERO_IMAGE, SERVICE_IMAGES } from "../lib/images";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { BookingModal } from "../components/BookingModal";
import { ConsultationModal } from "../components/ConsultationModal";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Star, ShieldCheck, Award, MapPin, Check, ArrowRight, CalendarClock,
  CreditCard, PackageCheck, Sparkles, Handshake,
} from "lucide-react";

const RIBBON = ["Licensed & Bonded in Florida", "White-Glove Discretion", "Realtor Preferred Partner", "Zero-Stress Move Guarantee", "Local Pensacola Crew, Never Outsourced"];

const HOW = [
  { icon: PackageCheck, title: "Choose a service", text: "Pick packing, unpacking, or organization — or request a custom staging consult." },
  { icon: CalendarClock, title: "Reserve your block", text: "Select an available service block and time that fits your move timeline." },
  { icon: CreditCard, title: "Secure with deposit", text: "Lock your appointment with a small Stripe deposit. Balance paid later via link." },
  { icon: Sparkles, title: "Enjoy turnkey peace", text: "Our local crew arrives on time and leaves your home flawless." },
];

const Reveal = ({ children, delay = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay }}>{children}</motion.div>
);

export default function Home() {
  const [services, setServices] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [booking, setBooking] = useState(false);
  const [consult, setConsult] = useState(false);
  const [presetService, setPresetService] = useState("");
  const [filter, setFilter] = useState("All");
  const [realtor, setRealtor] = useState({ name: "", brokerage: "", email: "", phone: "", listings_per_year: "" });
  const [realtorSent, setRealtorSent] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
    api.get("/portfolio").then((r) => setPortfolio(r.data));
    api.get("/testimonials").then((r) => setTestimonials(r.data));
  }, []);

  const openBooking = (sid = "") => { setPresetService(sid); setBooking(true); };
  const cats = ["All", ...Array.from(new Set(portfolio.map((p) => p.category)))];
  const shownPortfolio = filter === "All" ? portfolio : portfolio.filter((p) => p.category === filter);

  const submitRealtor = async (e) => {
    e.preventDefault();
    try {
      await api.post("/realtors", realtor);
      setRealtorSent(true);
      toast.success("Thanks! We'll reach out about the partner program.");
    } catch { toast.error("Could not submit. Please try again."); }
  };

  const spanFor = (id) => ({
    packing: "lg:col-span-7", unpacking: "lg:col-span-5",
    reorganizing: "lg:col-span-4", staging: "lg:col-span-4", "interior-rearrangement": "lg:col-span-4",
  }[id] || "lg:col-span-4");

  return (
    <div id="top" className="bg-brand-sand min-h-screen">
      <Header onBook={() => openBooking()} onConsult={() => setConsult(true)} />

      {/* HERO */}
      <section className="pt-[72px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-brand-gold mb-4">Pensacola & Emerald Coast Concierge</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-brand-navy leading-[1.1]">
              From Moving Chaos to <span className="text-brand-cerulean">Turnkey Peace</span> in Pensacola
            </h1>
            <p className="mt-5 text-base sm:text-lg text-brand-slate leading-relaxed max-w-xl">
              White-glove packing, unpacking, organization, staging, and interior rearrangement.
              A calm, cataloged, flawlessly-styled home — handled by a local crew who treats your things like their own.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button data-testid="hero-booking-btn" onClick={() => openBooking()}
                      className="brand-gold-shine bg-brand-cerulean hover:bg-brand-ceruleanDark text-white rounded-full h-12 px-8 text-base">
                Book a Service Block <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button data-testid="hero-consultation-btn" variant="outline" onClick={() => setConsult(true)}
                      className="border-brand-gold text-brand-goldHover hover:bg-brand-goldSoft rounded-full h-12 px-8 text-base">
                Get a Free Estimate
              </Button>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
              {[["500+", "Homes Transformed"], ["100%", "Insured & Bonded"], ["5.0★", "Gulf Coast Rating"]].map(([n, l]) => (
                <div key={l}>
                  <div className="font-serif text-2xl sm:text-3xl text-brand-cerulean font-semibold">{n}</div>
                  <div className="text-xs text-brand-slate mt-0.5">{l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative fade-up">
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img src={HERO_IMAGE} alt="Beautifully organized Pensacola coastal living room" className="w-full h-[440px] lg:h-[560px] object-cover" />
            </div>
            <div className="absolute top-5 left-5 bg-white/95 backdrop-blur rounded-2xl px-4 py-2.5 shadow-lg">
              <div className="text-xs font-semibold text-brand-navy">Same-Week Booking Available</div>
            </div>
            <div className="absolute bottom-5 right-5 bg-brand-ceruleanDark/95 backdrop-blur rounded-2xl px-4 py-3 shadow-lg text-white max-w-[220px]">
              <div className="flex items-center gap-1.5 text-brand-gold text-xs font-semibold"><MapPin className="w-3.5 h-3.5" /> Service Zones</div>
              <div className="text-xs text-white/80 mt-1">Pensacola Beach • East Hill • Gulf Breeze • Perdido Key</div>
            </div>
          </div>
        </div>

        {/* Trust ribbon */}
        <div className="bg-brand-goldSoft border-y border-brand-gold/20 overflow-hidden py-3">
          <div className="flex whitespace-nowrap animate-marquee">
            {[...RIBBON, ...RIBBON].map((r, i) => (
              <span key={i} className="mx-8 text-sm font-medium text-brand-goldHover flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" /> {r}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section id="services" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 scroll-mt-20">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-brand-gold mb-3">Our Services</div>
            <h2 className="font-serif text-3xl sm:text-4xl font-medium text-brand-navy">A full-service move & home concierge</h2>
            <p className="mt-3 text-brand-slate">Fixed service blocks for instant booking, plus custom consults for staging and rearrangement.</p>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {services.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.05}>
              <div data-testid={`service-card-${s.id}`}
                   className={`group relative rounded-2xl overflow-hidden border border-brand-border bg-white shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col`}
                   style={{ gridColumn: undefined }}>
                <div className="relative h-44 overflow-hidden">
                  <img src={SERVICE_IMAGES[s.id]} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-3 right-3 bg-white/95 rounded-full px-3 py-1 text-xs font-semibold text-brand-cerulean">{s.starting_price}</div>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-serif text-xl text-brand-navy">{s.name}</h3>
                  <p className="text-sm text-brand-slate mt-1.5 flex-1">{s.tagline}</p>
                  <ul className="mt-3 space-y-1.5">
                    {s.included?.slice(0, 3).map((inc) => (
                      <li key={inc} className="flex items-start gap-2 text-xs text-brand-slate"><Check className="w-3.5 h-3.5 text-brand-emerald mt-0.5 shrink-0" /> {inc}</li>
                    ))}
                  </ul>
                  <div className="mt-4">
                    {s.bookable ? (
                      <Button data-testid={`service-book-${s.id}`} onClick={() => openBooking(s.id)}
                              className="w-full bg-brand-cerulean hover:bg-brand-ceruleanDark text-white rounded-full">
                        Book — ${s.deposit} deposit
                      </Button>
                    ) : (
                      <Button data-testid={`service-consult-${s.id}`} variant="outline" onClick={() => setConsult(true)}
                              className="w-full border-brand-gold text-brand-goldHover hover:bg-brand-goldSoft rounded-full">
                        Request Consult
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <style>{`
          @media (min-width:1024px){
            #services .grid > div:nth-child(1){grid-column:span 7}
            #services .grid > div:nth-child(2){grid-column:span 5}
            #services .grid > div:nth-child(3){grid-column:span 4}
            #services .grid > div:nth-child(4){grid-column:span 4}
            #services .grid > div:nth-child(5){grid-column:span 4}
          }
        `}</style>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="bg-brand-ceruleanDark text-white py-16 lg:py-24 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <div className="text-xs uppercase tracking-[0.22em] font-semibold text-brand-gold mb-3">How It Works</div>
              <h2 className="font-serif text-3xl sm:text-4xl font-medium">Booked in minutes, handled in hours</h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {HOW.map((h, i) => (
              <Reveal key={h.title} delay={i * 0.08}>
                <div className="relative rounded-2xl bg-white/5 border border-white/10 p-6 h-full">
                  <div className="w-11 h-11 rounded-xl bg-brand-gold/20 flex items-center justify-center mb-4"><h.icon className="w-5 h-5 text-brand-gold" /></div>
                  <div className="text-brand-gold/70 text-xs font-semibold mb-1">STEP {i + 1}</div>
                  <h3 className="font-display font-semibold text-lg">{h.title}</h3>
                  <p className="text-sm text-white/70 mt-1.5">{h.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 scroll-mt-20">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-brand-gold mb-3">Pricing & Blocks</div>
            <h2 className="font-serif text-3xl sm:text-4xl font-medium text-brand-navy">Transparent starting rates</h2>
            <p className="mt-3 text-brand-slate">A refundable-toward-service deposit secures your block. The balance is billed after the job via a secure link.</p>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s) => (
            <div key={s.id} className="rounded-2xl border border-brand-border bg-white p-6 flex flex-col">
              <h3 className="font-serif text-xl text-brand-navy">{s.name}</h3>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-serif text-3xl text-brand-cerulean font-semibold">{s.starting_price.split(" ")[0]}</span>
                <span className="text-sm text-brand-slate">{s.starting_price.split(" ").slice(1).join(" ")}</span>
              </div>
              <div className="mt-1 text-sm text-brand-gold font-semibold">${s.deposit} deposit to book</div>
              <p className="text-xs text-brand-slate mt-3 flex-1">Ideal for: {s.ideal_for}</p>
              {s.bookable ? (
                <Button onClick={() => openBooking(s.id)} data-testid={`pricing-book-${s.id}`} className="mt-5 w-full bg-brand-cerulean hover:bg-brand-ceruleanDark text-white rounded-full">Book Now</Button>
              ) : (
                <Button onClick={() => setConsult(true)} data-testid={`pricing-consult-${s.id}`} variant="outline" className="mt-5 w-full border-brand-gold text-brand-goldHover rounded-full">Request Consult</Button>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* PORTFOLIO */}
      <section id="portfolio" className="bg-white py-16 lg:py-24 scroll-mt-20 border-y border-brand-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <div className="text-xs uppercase tracking-[0.22em] font-semibold text-brand-gold mb-3">Portfolio</div>
              <h2 className="font-serif text-3xl sm:text-4xl font-medium text-brand-navy">Recent transformations</h2>
            </div>
          </Reveal>
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {cats.map((c) => (
              <button key={c} onClick={() => setFilter(c)} data-testid={`portfolio-filter-${c.replace(/\s+/g,'-').toLowerCase()}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${filter === c ? "bg-brand-cerulean text-white" : "bg-brand-sand text-brand-slate hover:bg-brand-ceruleanLight"}`}>{c}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {shownPortfolio.map((p) => (
              <div key={p.id} data-testid={`portfolio-item-${p.id}`} className="group relative rounded-2xl overflow-hidden aspect-[4/3]">
                <img src={p.image_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-midnight/85 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-4">
                  <div className="text-xs text-brand-gold font-semibold">{p.category}</div>
                  <div className="text-white font-serif text-lg leading-tight">{p.title}</div>
                  <div className="text-xs text-white/70 mt-1">{p.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-brand-gold mb-3">Testimonials</div>
            <h2 className="font-serif text-3xl sm:text-4xl font-medium text-brand-navy">Loved across the Gulf Coast</h2>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-5">
          {testimonials.map((t) => (
            <div key={t.id} className="rounded-2xl border border-brand-border bg-white p-6">
              <div className="flex gap-0.5 mb-3">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="w-4 h-4 fill-brand-gold text-brand-gold" />)}</div>
              <p className="text-brand-navy leading-relaxed">"{t.quote}"</p>
              <div className="mt-4 pt-4 border-t border-brand-border">
                <div className="font-semibold text-brand-navy">{t.name}</div>
                <div className="text-xs text-brand-slate">{t.location}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* REALTOR */}
      <section id="realtors" className="bg-brand-ceruleanDark text-white py-16 lg:py-24 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-brand-gold mb-3">Realtor Partner Program</div>
            <h2 className="font-serif text-3xl sm:text-4xl font-medium">Sell faster with a staging-ready home</h2>
            <p className="mt-4 text-white/70">Refer clients for move and staging support and unlock priority scheduling, a 10% client discount voucher, co-branded marketing, and a staging debrief PDF for every listing.</p>
            <ul className="mt-6 space-y-3">
              {["Priority client scheduling", "10% client discount voucher", "Co-branded staging checklists", "Realtor staging debrief PDF"].map((p) => (
                <li key={p} className="flex items-center gap-3"><Award className="w-5 h-5 text-brand-gold" /> <span className="text-white/90">{p}</span></li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl bg-white p-6 lg:p-8 text-brand-navy">
            {realtorSent ? (
              <div className="text-center py-10">
                <Handshake className="w-12 h-12 text-brand-emerald mx-auto mb-3" />
                <h3 className="font-serif text-2xl">You're on the list!</h3>
                <p className="text-brand-slate mt-2">We'll be in touch about the partner program.</p>
              </div>
            ) : (
              <form onSubmit={submitRealtor} className="space-y-3">
                <h3 className="font-serif text-2xl mb-1">Become a partner</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Name</Label><Input data-testid="realtor-form-name" required value={realtor.name} onChange={(e)=>setRealtor({...realtor,name:e.target.value})} className="mt-1" /></div>
                  <div><Label>Brokerage</Label><Input data-testid="realtor-form-brokerage" required value={realtor.brokerage} onChange={(e)=>setRealtor({...realtor,brokerage:e.target.value})} className="mt-1" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Email</Label><Input data-testid="realtor-form-email" type="email" required value={realtor.email} onChange={(e)=>setRealtor({...realtor,email:e.target.value})} className="mt-1" /></div>
                  <div><Label>Cell</Label><Input data-testid="realtor-form-phone" required value={realtor.phone} onChange={(e)=>setRealtor({...realtor,phone:e.target.value})} className="mt-1" /></div>
                </div>
                <div><Label>Active listings / year</Label><Input data-testid="realtor-form-listings" value={realtor.listings_per_year} onChange={(e)=>setRealtor({...realtor,listings_per_year:e.target.value})} className="mt-1" /></div>
                <Button type="submit" data-testid="realtor-form-submit-btn" className="w-full bg-brand-gold hover:bg-brand-goldHover text-white rounded-full">Join the Program</Button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="rounded-3xl bg-gradient-to-br from-brand-cerulean to-brand-ceruleanDark p-10 lg:p-14 text-center text-white">
          <h2 className="font-serif text-3xl sm:text-4xl font-medium">Ready for a turnkey move?</h2>
          <p className="mt-3 text-white/80 max-w-xl mx-auto">Reserve a service block today or request a free photo estimate for custom work.</p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Button data-testid="cta-book-btn" onClick={() => openBooking()} className="brand-gold-shine bg-brand-gold hover:bg-brand-goldHover text-white rounded-full h-12 px-8">Book a Service Block</Button>
            <Button data-testid="cta-consult-btn" variant="outline" onClick={() => setConsult(true)} className="border-white/40 text-white hover:bg-white/10 rounded-full h-12 px-8">Get a Free Estimate</Button>
          </div>
        </div>
      </section>

      <Footer />
      <BookingModal open={booking} onClose={() => setBooking(false)} presetServiceId={presetService} />
      <ConsultationModal open={consult} onClose={() => setConsult(false)} />
    </div>
  );
}
